import { uniq } from '@/features/import/normalizers';
import type { EdgeRecord, RouteCandidate, RouteMode, SolverOptions, SolverResult } from '@/features/network/models';

const MAX_CANDIDATES_EXPLORED = 6000;
const SEARCH_PROGRESS_SOFT_LIMIT = MAX_CANDIDATES_EXPLORED * 2;
const DEFAULT_ROUTE_WEIGHTS = {
  latency: 50,
  cost: 30,
  hops: 20,
};

export type SolveRoutesPhase = 'preparing' | 'searching' | 'ranking' | 'completed';

export type SolveRoutesProgress = {
  phase: SolveRoutesPhase;
  percent: number;
  message: string;
  exploredStates?: number;
  foundRoutes?: number;
};

type SolveRoutesCallbacks = {
  onProgress?: (progress: SolveRoutesProgress) => void;
};

function clampPercent(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function safeRouteWeights(options: SolverOptions) {
  const routeWeights = (options as Partial<SolverOptions>).routeWeights;
  const safeNumber = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

  return {
    latency: safeNumber(routeWeights?.latency, DEFAULT_ROUTE_WEIGHTS.latency),
    cost: safeNumber(routeWeights?.cost, DEFAULT_ROUTE_WEIGHTS.cost),
    hops: safeNumber(routeWeights?.hops, DEFAULT_ROUTE_WEIGHTS.hops),
  };
}

export function effectiveLatency(edge: EdgeRecord): number | null {
  return edge.expectedRtdMs ?? edge.slaRtdMs ?? null;
}

function safeMetricSum(values: Array<number | null>): number | null {
  const known = values.filter((v): v is number => v != null && Number.isFinite(v));
  if (known.length === 0) return null;
  return known.reduce((acc, value) => acc + value, 0);
}

function buildAdjacency(edges: EdgeRecord[]) {
  const map = new Map<string, Array<{ node: string; edge: EdgeRecord }>>();

  function add(from: string, to: string, edge: EdgeRecord) {
    if (!map.has(from)) map.set(from, []);
    map.get(from)!.push({ node: to, edge });
  }

  for (const edge of edges) {
    add(edge.from, edge.to, edge);
    add(edge.to, edge.from, edge);
  }

  return map;
}

function edgeUniqueKey(edge: EdgeRecord, a: string, b: string) {
  const left = [a, b].sort().join(' ⇄ ');
  return `${edge.id}::${left}`;
}

function orderedWaypointsPassed(nodes: string[], orderedWaypoints: string[]) {
  const passed: string[] = [];
  let cursor = 0;
  for (const node of nodes) {
    if (orderedWaypoints[cursor] && node === orderedWaypoints[cursor]) {
      passed.push(node);
      cursor += 1;
    }
  }
  return passed;
}

function routeSatisfiesWaypoints(nodes: string[], orderedWaypoints: string[]) {
  const passed = orderedWaypointsPassed(nodes, orderedWaypoints);
  return passed.length === orderedWaypoints.length;
}

function evaluatePath(edges: EdgeRecord[], nodes: string[], options: SolverOptions): RouteCandidate {
  const totalLatencyMs = safeMetricSum(edges.map(effectiveLatency));
  const totalMrc = edges.reduce((acc, edge) => acc + (edge.mrc ?? 0), 0);
  const totalNrc = edges.reduce((acc, edge) => acc + (edge.nrc ?? 0), 0);
  const effectiveMonthlyCost = totalMrc + totalNrc / Math.max(1, options.amortizationMonths);
  const capacities = edges.map((edge) => edge.capacityMbps).filter((v): v is number => v != null);
  const bottleneckMbps = capacities.length ? Math.min(...capacities) : null;
  const warnings = uniq(edges.flatMap((edge) => edge.notes));

  return {
    id: `${nodes.join('__')}__${edges.map((edge) => edge.id).join('__')}`,
    nodes,
    edges,
    totalLatencyMs,
    totalMrc,
    totalNrc,
    effectiveMonthlyCost,
    hops: edges.length,
    bottleneckMbps,
    vendors: uniq(edges.map((edge) => edge.vendorName).filter(Boolean)),
    statuses: uniq(edges.map((edge) => edge.status).filter(Boolean)),
    serviceTypes: uniq(edges.map((edge) => edge.serviceType).filter(Boolean)),
    score: 0,
    warnings,
    edgeKeys: edges.map((edge, index) => edgeUniqueKey(edge, nodes[index], nodes[index + 1])),
    passedWaypoints: orderedWaypointsPassed(nodes, options.orderedWaypoints),
  };
}

function dominates(a: RouteCandidate, b: RouteCandidate) {
  const aLatency = a.totalLatencyMs ?? Number.POSITIVE_INFINITY;
  const bLatency = b.totalLatencyMs ?? Number.POSITIVE_INFINITY;

  const noWorse = aLatency <= bLatency && a.effectiveMonthlyCost <= b.effectiveMonthlyCost && a.hops <= b.hops;
  const strictlyBetter = aLatency < bLatency || a.effectiveMonthlyCost < b.effectiveMonthlyCost || a.hops < b.hops;

  return noWorse && strictlyBetter;
}

function computeScore(route: RouteCandidate, all: RouteCandidate[], mode: Exclude<RouteMode, 'pareto'>, options: SolverOptions): number {
  if (mode === 'latency') return route.totalLatencyMs ?? Number.POSITIVE_INFINITY;
  if (mode === 'cost') return route.effectiveMonthlyCost;
  if (mode === 'hops') return route.hops;

  const latencyValues = all.map((item) => item.totalLatencyMs ?? 999999);
  const costValues = all.map((item) => item.effectiveMonthlyCost);
  const hopValues = all.map((item) => item.hops);

  const minLatency = Math.min(...latencyValues);
  const maxLatency = Math.max(...latencyValues);
  const minCost = Math.min(...costValues);
  const maxCost = Math.max(...costValues);
  const minHops = Math.min(...hopValues);
  const maxHops = Math.max(...hopValues);

  const latencyNorm = ((route.totalLatencyMs ?? 999999) - minLatency) / Math.max(1, maxLatency - minLatency);
  const costNorm = (route.effectiveMonthlyCost - minCost) / Math.max(1, maxCost - minCost);
  const hopsNorm = (route.hops - minHops) / Math.max(1, maxHops - minHops);
  const missingPenalty = route.totalLatencyMs == null ? 0.35 : 0;
  const weights = safeRouteWeights(options);
  const weightsSum = Math.max(1, weights.latency + weights.cost + weights.hops);

  return (
    latencyNorm * (weights.latency / weightsSum) +
    costNorm * (weights.cost / weightsSum) +
    hopsNorm * (weights.hops / weightsSum) +
    missingPenalty * 0.05
  );
}

function violatesGlobalConstraints(pathEdges: EdgeRecord[], options: SolverOptions) {
  const totalLatency = safeMetricSum(pathEdges.map(effectiveLatency));
  const totalMrc = pathEdges.reduce((acc, edge) => acc + (edge.mrc ?? 0), 0);
  const totalNrc = pathEdges.reduce((acc, edge) => acc + (edge.nrc ?? 0), 0);
  const effectiveMonthlyCost = totalMrc + totalNrc / Math.max(1, options.amortizationMonths);

  if (options.maxTotalLatencyMs != null && totalLatency != null && totalLatency > options.maxTotalLatencyMs) return true;
  if (options.maxEffectiveMonthlyCost != null && effectiveMonthlyCost > options.maxEffectiveMonthlyCost) return true;
  return false;
}

export function solveRoutes(edges: EdgeRecord[], options: SolverOptions, callbacks: SolveRoutesCallbacks = {}): SolverResult {
  const emitProgress = (progress: SolveRoutesProgress) => {
    callbacks.onProgress?.({
      ...progress,
      percent: clampPercent(progress.percent),
    });
  };

  emitProgress({
    phase: 'preparing',
    percent: 3,
    message: 'Подготовка данных маршрутизации',
    exploredStates: 0,
    foundRoutes: 0,
  });

  if (!options.source || !options.target || options.source === options.target) {
    emitProgress({
      phase: 'completed',
      percent: 100,
      message: 'Расчёт завершён',
      exploredStates: 0,
      foundRoutes: 0,
    });
    return { primary: null, backup: null, routes: [] };
  }

  const avoidSet = new Set(options.avoidLocations.filter(Boolean));
  const waypointSet = new Set(options.orderedWaypoints.filter(Boolean));

  const filteredEdges = edges.filter((edge) => {
    const statusPass = options.allowedStatuses.length === 0 || options.allowedStatuses.includes(edge.status);
    const serviceTypePass = options.serviceTypeFilter.length === 0 || options.serviceTypeFilter.includes(edge.serviceType);
    const capacityPass = (edge.capacityMbps ?? 0) >= options.minCapacityMbps;
    const latencyPass = options.includeUnknownLatency || effectiveLatency(edge) != null || options.mode === 'cost' || options.mode === 'hops';
    const avoidPass =
      (!avoidSet.has(edge.from) || edge.from === options.source || edge.from === options.target || waypointSet.has(edge.from)) &&
      (!avoidSet.has(edge.to) || edge.to === options.source || edge.to === options.target || waypointSet.has(edge.to));

    return statusPass && serviceTypePass && capacityPass && latencyPass && avoidPass;
  });

  const adjacency = buildAdjacency(filteredEdges);
  const visitedNodes = new Set<string>();
  const found: RouteCandidate[] = [];
  let exploredStates = 0;
  let lastEmittedPercent = -1;

  const emitSearchProgress = (force = false) => {
    const searchProgress = 10 + (Math.min(exploredStates, SEARCH_PROGRESS_SOFT_LIMIT) / SEARCH_PROGRESS_SOFT_LIMIT) * 70;
    const percent = clampPercent(searchProgress);
    if (!force && percent <= lastEmittedPercent) return;

    lastEmittedPercent = percent;
    emitProgress({
      phase: 'searching',
      percent,
      message: `Поиск вариантов: найдено ${found.length}`,
      exploredStates,
      foundRoutes: found.length,
    });
  };

  emitProgress({
    phase: 'searching',
    percent: 10,
    message: 'Запуск обхода графа',
    exploredStates,
    foundRoutes: found.length,
  });

  function dfs(current: string, target: string, pathNodes: string[], pathEdges: EdgeRecord[]) {
    exploredStates += 1;
    if (exploredStates % 80 === 0) {
      emitSearchProgress();
    }

    if (found.length >= MAX_CANDIDATES_EXPLORED) return;
    if (pathEdges.length > options.maxHops) return;
    if (violatesGlobalConstraints(pathEdges, options)) return;

    if (current === target) {
      if (!routeSatisfiesWaypoints(pathNodes, options.orderedWaypoints)) return;
      found.push(evaluatePath(pathEdges, [...pathNodes], options));
      emitSearchProgress(true);
      return;
    }

    const neighbors = adjacency.get(current) || [];
    for (const { node, edge } of neighbors) {
      if (visitedNodes.has(node)) continue;
      if (avoidSet.has(node) && node !== options.target && !waypointSet.has(node)) continue;
      visitedNodes.add(node);
      pathNodes.push(node);
      pathEdges.push(edge);
      dfs(node, target, pathNodes, pathEdges);
      pathEdges.pop();
      pathNodes.pop();
      visitedNodes.delete(node);
    }
  }

  visitedNodes.add(options.source);
  dfs(options.source, options.target, [options.source], []);

  emitProgress({
    phase: 'ranking',
    percent: 88,
    message: `Оценка и ранжирование ${found.length} маршрутов`,
    exploredStates,
    foundRoutes: found.length,
  });

  let routes = found;
  if (options.mode === 'pareto') {
    routes = routes.filter((route, index) => !routes.some((other, otherIndex) => index !== otherIndex && dominates(other, route)));
  }

  const scoreMode: Exclude<RouteMode, 'pareto'> = options.mode === 'pareto' ? 'balanced' : options.mode;
  routes = routes
    .map((route) => ({ ...route, score: computeScore(route, routes.length > 0 ? routes : [route], scoreMode, options) }))
    .sort((a, b) => a.score - b.score)
    .slice(0, options.topK);

  const primary = routes[0] ?? null;
  let backup: RouteCandidate | null = null;

  if (primary) {
    const primaryEdgeSet = new Set(primary.edgeKeys);
    backup =
      routes.find((route) => route.id !== primary.id && route.edgeKeys.every((key) => !primaryEdgeSet.has(key))) ??
      routes.find((route) => route.id !== primary.id) ??
      null;
  }

  emitProgress({
    phase: 'completed',
    percent: 100,
    message: `Расчёт завершён: ${routes.length} маршрутов`,
    exploredStates,
    foundRoutes: routes.length,
  });

  return { primary, backup, routes };
}
